import { useEffect, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { toast } from "react-toastify"
import {
  useGetAdAccountsQuery,
  useGetCurrentAdAccountQuery,
  useSetAdAccountMutation,
} from "~/ui-rtk/api/campaignBlasterApi"
import {
  useGetMetaAccessQuery,
  useSetMetaAccountAssignmentPausedMutation,
  useUnassignMetaAccountMutation,
} from "~/ui-rtk/api/metaAccountApi"
import type { MetaAccess } from "~/ui-rtk/api/types/meta-account.types"
// Direct imports (not the `common` / `controls` barrels) — see CreativesTab.tsx.
import Divider from "~/ui-rtk/components/ui/common/Divider"
import Select from "~/ui-rtk/components/ui/controls/Select"
import H3 from "~/ui-rtk/components/ui/typography/H3"
import useAccess from "~/ui-rtk/hooks/access"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"
import { getServerErrorMessage } from "~/ui-rtk/utils/http-utils"
import AccessChip from "./AccessChip"
import AssignMetaAccessDialog from "./AssignMetaAccessDialog"
import {
  BINDING_LABEL,
  BINDING_NO_ACCOUNTS,
  BINDING_PLACEHOLDER,
  BINDING_SAVED,
  BINDING_SAVE_FAILED,
  BUTTON,
  CAPABILITY_LINE_LABEL,
  CHANGE_FAILED,
  DISCOVERY_LINE_LABEL,
  META_ACCESS_EMPTY,
  META_ACCESS_LOAD_ERROR,
  META_ACCESS_MANAGE,
  META_ACCESS_PARAM,
  META_ACCESS_TITLE,
  NONE,
  ROW_LABEL,
  TOAST,
  adAccountLine,
  capabilityServedText,
  discoveryServedText,
  instagramLine,
  lastCheckedText,
  metaAccessIntro,
  namedLine,
  onDayBy,
  optionLabel,
  unassignConfirmText,
} from "./copy"
import ManageAccountUsersDialog from "./ManageAccountUsersDialog"
import { assignmentStatusChipOf } from "./meta-access.utils"

const ACTION_CLS =
  "text-gray-400 hover:text-gray-200 text-sm underline cursor-pointer transition-colors disabled:opacity-50"

/** The binding is stored as `act_<id>` or bare: one account either way. */
function bareOf(adAccountId: string): string {
  return adAccountId.replace(/^act_/, "")
}

/**
 * The company's ad-account binding (APP-589), on the routes of the old Meta Ad
 * Account card: GET /campaign-blaster/ad-account reads it, GET
 * /campaign-blaster/ad-accounts lists what the company's token sees, POST
 * /campaign-blaster/ad-account binds one (the API refuses an account another
 * company holds or one that differs from the assignment). A save refreshes the
 * card: `setAdAccount` invalidates Meta access, so the Assign pre-fill follows.
 */
function AdAccountBindingLine() {
  const { data: current, isLoading: isCurrentLoading } =
    useGetCurrentAdAccountQuery()
  const { data: accounts, isLoading: isAccountsLoading } =
    useGetAdAccountsQuery()
  const [saveAdAccount, { isLoading: saving }] = useSetAdAccountMutation()

  const [editing, setEditing] = useState(false)
  const [picked, setPicked] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const savedBare = current?.adAccountId ? bareOf(current.adAccountId) : null
  const savedEntry = savedBare
    ? accounts?.find(({ id }) => bareOf(id) === savedBare)
    : undefined
  // Starts on the bound account; with none, on the placeholder: the picker
  // never picks an account itself.
  const value = picked ?? savedEntry?.id ?? ""
  const changed = value !== "" && bareOf(value) !== savedBare

  const close = () => {
    setEditing(false)
    setPicked(null)
    setError(null)
  }

  const save = async () => {
    setError(null)
    try {
      await saveAdAccount({ adAccountId: value }).unwrap()
      close()
      toast.success(BINDING_SAVED)
    } catch (err) {
      setError(getServerErrorMessage(err, BINDING_SAVE_FAILED))
    }
  }

  let picker: JSX.Element | null = null
  if (editing && isAccountsLoading) {
    picker = (
      <span
        data-testid="meta-access-binding-accounts-loading"
        className="loading loading-spinner loading-sm text-primary"
      />
    )
  } else if (editing && (!accounts || accounts.length === 0)) {
    picker = (
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-system-gray-400">{BINDING_NO_ACCOUNTS}</p>
        <button type="button" className={ACTION_CLS} onClick={close}>
          {BUTTON.cancel}
        </button>
      </div>
    )
  } else if (editing && accounts) {
    picker = (
      <div className="flex flex-wrap items-center gap-2">
        <Select
          aria-label={BINDING_LABEL}
          className="select-sm w-auto max-w-full text-sm"
          options={accounts.map(({ id, name }) => ({
            label: optionLabel(id, name),
            value: id,
          }))}
          placeholder={BINDING_PLACEHOLDER}
          value={value}
          onChange={({ target }) => setPicked(target.value)}
          disabled={saving}
        />
        <button
          type="button"
          className="btn btn-primary btn-xs"
          disabled={!changed || saving}
          onClick={() => void save()}
        >
          {saving && <span className="loading loading-spinner loading-xs" />}
          {BUTTON.save}
        </button>
        <button
          type="button"
          className="btn btn-ghost btn-xs"
          disabled={saving}
          onClick={close}
        >
          {BUTTON.cancel}
        </button>
      </div>
    )
  }

  return (
    <div
      data-testid="meta-access-binding"
      className="flex flex-col gap-2 text-sm"
    >
      <p className="flex flex-wrap items-center gap-1">
        <span className="text-system-gray-400">{`${BINDING_LABEL}:`}</span>
        {isCurrentLoading ? (
          <span
            data-testid="meta-access-binding-loading"
            className="loading loading-spinner loading-xs text-primary"
          />
        ) : (
          <span className="text-white">
            {savedBare
              ? adAccountLine(savedBare, savedEntry?.name ?? null)
              : NONE}
          </span>
        )}
        {!editing && (
          <button
            type="button"
            className={`ml-2 ${ACTION_CLS}`}
            onClick={() => setEditing(true)}
          >
            {BUTTON.change}
          </button>
        )}
      </p>
      {picker}
      {error && (
        <p role="alert" className="text-states-error">
          {error}
        </p>
      )}
    </div>
  )
}

function DiscoveryLine({ access }: { access: MetaAccess }) {
  const discovery = access.capabilities.find(
    ({ capability }) => capability === "discovery",
  )
  if (!discovery) return null
  return (
    <div
      data-testid="meta-access-discovery"
      className="flex flex-col gap-1 text-sm"
    >
      <p className="flex flex-wrap gap-1">
        <span className="text-system-gray-400">{`${DISCOVERY_LINE_LABEL}:`}</span>
        <span className="text-white">
          {discoveryServedText(discovery.servedBy)}
        </span>
      </p>
      {discovery.skipped.map(({ candidate, reason, detail }) => (
        <p key={`${candidate}-${reason}`} className="text-system-gray-400">
          {detail}
        </p>
      ))}
    </div>
  )
}

/**
 * The resolver capabilities tier 2 may serve (APP-566 T5): one line per row
 * the API sends after discovery (only while the tier order is on), then each
 * skip's detail verbatim.
 */
function CapabilityLines({ access }: { access: MetaAccess }) {
  return (
    <>
      {access.capabilities.map(({ capability, servedBy, skipped }) =>
        capability === "discovery" ? null : (
          <div
            key={capability}
            data-testid={`meta-access-capability-${capability}`}
            className="flex flex-col gap-1 text-sm"
          >
            <p className="flex flex-wrap gap-1">
              <span className="text-system-gray-400">{`${CAPABILITY_LINE_LABEL[capability]}:`}</span>
              <span className="text-white">
                {capabilityServedText(servedBy)}
              </span>
            </p>
            {skipped.map(({ candidate, reason, detail }) => (
              <p
                key={`${candidate}-${reason}`}
                className="text-system-gray-400"
              >
                {detail}
              </p>
            ))}
          </div>
        ),
      )}
    </>
  )
}

function AssignmentRows({ access }: { access: MetaAccess }) {
  const { assignment, assetNames } = access
  if (!assignment) return null
  const chip = assignmentStatusChipOf(assignment)
  return (
    <dl
      data-testid="meta-access-assignment"
      className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm"
    >
      <dt className="text-system-gray-400">{ROW_LABEL.accountUser}</dt>
      <dd className="flex flex-wrap items-center gap-2 text-white">
        <span>{assignment.credentialName ?? NONE}</span>
        <AccessChip tone={chip.tone} title={chip.title}>
          {chip.label}
        </AccessChip>
      </dd>
      <dt className="text-system-gray-400">{ROW_LABEL.adAccount}</dt>
      <dd className="text-white">
        {assignment.adAccountId
          ? adAccountLine(assignment.adAccountId, assetNames.adAccount)
          : NONE}
      </dd>
      <dt className="text-system-gray-400">{ROW_LABEL.instagram}</dt>
      <dd className="text-white">
        {assignment.igUserId
          ? instagramLine(assignment.igUserId, assetNames.instagram)
          : NONE}
      </dd>
      <dt className="text-system-gray-400">{ROW_LABEL.page}</dt>
      <dd className="text-white">
        {assignment.pageId
          ? namedLine(assignment.pageId, assetNames.page)
          : NONE}
      </dd>
      <dt className="text-system-gray-400">{ROW_LABEL.business}</dt>
      <dd className="text-white">
        {assignment.businessId
          ? namedLine(assignment.businessId, assetNames.business)
          : NONE}
      </dd>
      <dt className="text-system-gray-400">{ROW_LABEL.assigned}</dt>
      <dd>{onDayBy(assignment.assignedAt, assignment.assignedByName)}</dd>
      <dt className="text-system-gray-400">{ROW_LABEL.lastChecked}</dt>
      <dd>{lastCheckedText(assignment.lastCheckedAt)}</dd>
    </dl>
  )
}

/**
 * Settings → Company → Meta access (APP-566 T2), super admins only: the
 * company's ad-account binding (APP-589), which account user (a Marathon
 * person's Facebook login) this company is assigned, on which assets, and what
 * partnership discovery runs on as a result. Every read names the company
 * explicitly: tabs share the session, so the card must always describe the
 * company it renders.
 */
export default function MetaAccessSettings() {
  // Defense in depth: CompanyPage mounts this behind `{isSuperAdmin && ...}`
  // too; checking here keeps it closed if it is mounted somewhere new.
  const { isSuperAdmin } = useAccess()
  const company = useCurrentCompany()
  const companyId = company?.id ?? ""
  const companyName = company?.name ?? ""

  const {
    data: access,
    isLoading,
    isError,
  } = useGetMetaAccessQuery(
    { companyId },
    { skip: !isSuperAdmin || !companyId },
  )
  const [setPaused, { isLoading: pausing }] =
    useSetMetaAccountAssignmentPausedMutation()
  const [unassign, { isLoading: unassigning }] =
    useUnassignMetaAccountMutation()

  const [searchParams, setSearchParams] = useSearchParams()
  const [manageOpen, setManageOpen] = useState(false)
  const [assignOpen, setAssignOpen] = useState(false)
  const [confirmUnassign, setConfirmUnassign] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const busy = pausing || unassigning

  // Opened by a link (an alert, or the way back from Facebook's dialog): open
  // Manage once and drop the param, so a reload or Back does not reopen it.
  useEffect(() => {
    if (
      !isSuperAdmin ||
      searchParams.get(META_ACCESS_PARAM) !== META_ACCESS_MANAGE
    ) {
      return
    }
    setManageOpen(true)
    const next = new URLSearchParams(searchParams)
    next.delete(META_ACCESS_PARAM)
    setSearchParams(next, { replace: true })
  }, [isSuperAdmin, searchParams])

  if (!isSuperAdmin) return null

  const assignment = access?.assignment ?? null

  const change = async (
    action: () => Promise<unknown>,
    success: string,
  ): Promise<void> => {
    setError(null)
    try {
      await action()
      setConfirmUnassign(false)
      toast.success(success)
    } catch (err) {
      setError(getServerErrorMessage(err, CHANGE_FAILED))
    }
  }

  const togglePause = () => {
    if (!assignment) return
    const paused = assignment.pausedAt === null
    void change(
      () => setPaused({ companyId, paused }).unwrap(),
      paused ? TOAST.paused(companyName) : TOAST.resumed(companyName),
    )
  }

  let body: JSX.Element | null = null
  if (isLoading) {
    body = (
      <span
        data-testid="meta-access-loading"
        className="loading loading-spinner loading-md text-primary"
      />
    )
  } else if (isError) {
    body = <p className="text-sm text-states-error">{META_ACCESS_LOAD_ERROR}</p>
  } else if (access) {
    body = (
      <div className="flex flex-col gap-3">
        <DiscoveryLine access={access} />
        <CapabilityLines access={access} />
        {assignment ? (
          <AssignmentRows access={access} />
        ) : (
          <p className="text-sm text-system-gray-300">{META_ACCESS_EMPTY}</p>
        )}
        <div className="flex flex-wrap items-center gap-3">
          {assignment ? (
            <>
              <button
                type="button"
                className={ACTION_CLS}
                disabled={busy}
                onClick={() => setAssignOpen(true)}
              >
                {BUTTON.edit}
              </button>
              <button
                type="button"
                className={ACTION_CLS}
                disabled={busy}
                onClick={togglePause}
              >
                {assignment.pausedAt === null ? BUTTON.pause : BUTTON.resume}
              </button>
              <button
                type="button"
                className={ACTION_CLS}
                disabled={busy}
                onClick={() => {
                  setError(null)
                  setConfirmUnassign(true)
                }}
              >
                {BUTTON.unassign}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={busy}
              onClick={() => setAssignOpen(true)}
            >
              {BUTTON.assign}
            </button>
          )}
        </div>
        {assignment && confirmUnassign && (
          <div
            role="alertdialog"
            aria-label="Confirm unassign"
            className="flex flex-col gap-2 rounded-md border border-system-red-600 bg-system-red-900/30 px-system-md py-system-sm text-sm text-system-red-100"
          >
            <span>{unassignConfirmText(companyName)}</span>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmUnassign(false)}
                disabled={busy}
                className="btn btn-ghost btn-xs"
              >
                {BUTTON.keepIt}
              </button>
              <button
                type="button"
                onClick={() =>
                  void change(
                    () => unassign({ companyId }).unwrap(),
                    TOAST.removed(companyName),
                  )
                }
                disabled={busy}
                className="btn btn-error btn-xs"
              >
                {unassigning && (
                  <span className="loading loading-spinner loading-xs" />
                )}
                {BUTTON.unassign}
              </button>
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <>
      <Divider className="my-3" />
      <H3>{META_ACCESS_TITLE}</H3>
      <p className="mt-1 text-sm text-system-gray-400">
        {metaAccessIntro(access?.flags.tier2 ?? [])}
      </p>
      <div className="flex flex-col gap-3 py-3">
        <AdAccountBindingLine />
        {body}
        {error && (
          <p role="alert" className="text-sm text-states-error">
            {error}
          </p>
        )}
        <div>
          <button
            type="button"
            className="btn btn-outline btn-sm"
            onClick={() => setManageOpen(true)}
          >
            {BUTTON.manage}
          </button>
        </div>
      </div>

      {manageOpen && (
        <ManageAccountUsersDialog onClose={() => setManageOpen(false)} />
      )}
      {assignOpen && access && (
        <AssignMetaAccessDialog
          companyId={companyId}
          companyName={companyName}
          connector={access.connector}
          assignment={assignment}
          onClose={() => setAssignOpen(false)}
        />
      )}
    </>
  )
}
